import { Link } from 'react-router-dom'
import {
  FileSearch,
  BarChart3,
  Shield,
  Landmark,
  ArrowRight,
  Database,
  Users,
  CalendarDays,
} from 'lucide-react'

const features = [
  {
    icon: FileSearch,
    title: 'Ogłoszenia BZP',
    description:
      'Przeszukuj i filtruj tysiące ogłoszeń z Biuletynu Zamówień Publicznych w jednym miejscu.',
  },
  {
    icon: BarChart3,
    title: 'Statystyki i analizy',
    description:
      'Analizuj wykonawców, zamawiających i trendy rynkowe na podstawie rzeczywistych danych.',
  },
  {
    icon: Shield,
    title: 'Bezpieczny dostęp',
    description:
      'Dane chronione autoryzacją. Twoje wyszukiwania i analizy pozostają prywatne.',
  },
]

const stats = [
  { icon: Database, value: '149 000+', label: 'ogłoszeń BZP' },
  { icon: Users, value: '118 000+', label: 'wykonawców' },
  { icon: CalendarDays, value: 'od 2024', label: 'ciągły monitoring' },
]

export default function LandingPage() {
  return (
    <div className="min-h-screen flex flex-col">
      {/* Hero */}
      <section className="relative flex-1 flex flex-col items-center justify-center px-6 py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-[#1A2F28] text-white overflow-hidden">
        {/* Subtle grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.3) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />

        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center mb-8">
            <div className="p-3 rounded-2xl bg-white/10 backdrop-blur-sm">
              <Landmark className="h-10 w-10 text-brand-dim" />
            </div>
          </div>

          <h1 className="text-5xl sm:text-6xl font-bold tracking-tight mb-6">
            Bid
            <span className="text-brand">Desk</span>
          </h1>

          <p className="text-xl sm:text-2xl text-slate-300 mb-4 font-light">
            Inteligentna analiza zamówień publicznych
          </p>

          <p className="text-base text-slate-400 mb-10 max-w-xl mx-auto">
            Monitoruj ogłoszenia BZP, analizuj wykonawców i zamawiających.
            Wszystkie dane w jednym miejscu.
          </p>

          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-slate-900 font-semibold rounded-xl hover:bg-brand-bg transition-colors"
          >
            Zaloguj się
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Decorative gradient blob */}
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-brand/20 rounded-full blur-3xl" />
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-slate-500/10 rounded-full blur-3xl" />
      </section>

      {/* Features */}
      <section className="py-20 px-6 bg-surface">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-ink text-center mb-4">
            Wszystko czego potrzebujesz
          </h2>
          <p className="text-ink-muted text-center mb-14 max-w-2xl mx-auto">
            Kompleksowa platforma do monitorowania i analizy zamówień publicznych w Polsce.
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature) => {
              const Icon = feature.icon
              return (
                <div
                  key={feature.title}
                  className="bg-surface-raised rounded-lg p-8 border border-border-subtle hover:border-border transition-colors"
                >
                  <div className="inline-flex p-3 rounded-xl bg-brand-bg mb-5">
                    <Icon className="h-6 w-6 text-brand" />
                  </div>
                  <h3 className="text-lg font-semibold text-ink mb-2">{feature.title}</h3>
                  <p className="text-ink-muted text-sm leading-relaxed">{feature.description}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="py-16 px-6 bg-surface-raised border-t border-border-subtle">
        <div className="max-w-4xl mx-auto grid sm:grid-cols-3 gap-10 text-center">
          {stats.map((stat) => {
            const Icon = stat.icon
            return (
              <div key={stat.label}>
                <Icon className="h-6 w-6 text-brand mx-auto mb-3" />
                <div className="text-3xl font-bold text-ink">{stat.value}</div>
                <div className="text-sm text-ink-muted mt-1">{stat.label}</div>
              </div>
            )
          })}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-slate-900 text-slate-400 text-sm">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <Landmark className="h-4 w-4 text-slate-500" />
            <span>&copy; 2026 BidDesk</span>
          </div>
          <div>info@biddesk.eu</div>
        </div>
      </footer>
    </div>
  )
}
